import React, { useEffect, useState } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import CloudSyncIcon from "@mui/icons-material/CloudSync";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css"; // Importar o CSS do Toastify
import { getSharedFoldersByPath, getUserFoldersAndFiles } from "../api";
import { useUserContext } from "../Context/UserContext";

interface AppToolbarProps {
  selectedItems: Set<string>;
  onDownloadFile: (filePath: string) => void;
  onUploadFile: () => void;
  onCreateFolder: () => void;
  fetchFolders: (path?: string) => Promise<void>; // Recebe o método fetchFolders do componente User
  onStatusChange: (message: string | null) => void;
}

const AppToolbar: React.FC<AppToolbarProps> = ({
  selectedItems,
  onDownloadFile,
  onUploadFile,
  onCreateFolder,
  fetchFolders,
  onStatusChange,
}) => {
  const { triggerUpdate, setBreadcrumb } = useUserContext(); // Acesse setBreadcrumb do contexto
  const [syncDisabled, setSyncDisabled] = useState(false);
  const { currentPath, updatePathAndBreadcrumb } = useUserContext();
  const rootFolder = window._env_?.REACT_APP_ROOT_FOLDER || ""; // Define a rootFolder
  const normalizePath = (path: string) => path.replace(/\\/g, "/").trim();
  const isInRootFolder =
    !currentPath || normalizePath(currentPath) === normalizePath(rootFolder);

  useEffect(() => {}, [currentPath, rootFolder]);

  const isFileSelected = (): boolean => {
    if (selectedItems.size !== 1) return false;

    const selectedItem = Array.from(selectedItems)[0];
    return selectedItem.includes(".");
  };

  const handleDownloadClick = () => {
    const filePath = Array.from(selectedItems)[0];
    onDownloadFile(filePath);
  };

  const handleSync = async () => {
    if (syncDisabled) return;

    try {
      const user = JSON.parse(sessionStorage.getItem("user") || "{}");

      if (!user?.login) {
        toast.error("Usuário não autenticado.");
        return;
      }

      const pathBeforeSync = currentPath;

      // 🔁 Atualiza apenas a pasta atual
      const sharedItems = await getSharedFoldersByPath({
        username: user.login,
        path: pathBeforeSync,
      });

      const fixedItems = sharedItems.map((item) => ({
        ...item,
        path: item.path.replace(/\\\\/g, "\\"),
      }));

      // 🔁 Substitui apenas os itens filhos da pasta atual
      const storedItems: any[] = JSON.parse(
        sessionStorage.getItem("userFolders") || "[]"
      );

      const updatedItems = [
        ...storedItems.filter(
          (item) =>
            !item.path.startsWith(pathBeforeSync + "\\") &&
            item.path !== pathBeforeSync
        ),
        ...fixedItems,
      ];

      sessionStorage.setItem("userFolders", JSON.stringify(updatedItems));

      await fetchFolders(pathBeforeSync); // Atualiza a visualização
      triggerUpdate();

      toast.success("Os arquivos foram atualizados.");
      setSyncDisabled(true);
      setTimeout(() => setSyncDisabled(false), 30000);
    } catch (error) {
      console.error("Erro ao sincronizar pastas e arquivos:", error);
      toast.error("Erro ao sincronizar os arquivos.");
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        backgroundColor: "transparent",
      }}
    >
      <Tooltip
        title={
          isInRootFolder
            ? "Local inválido para baixar arquivos."
            : "Baixar (Download)"
        }
        arrow
        placement="top"
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            cursor: isInRootFolder
              ? "not-allowed"
              : isFileSelected()
                ? "pointer"
                : "default",
            color: isFileSelected() ? "inherit" : "gray",
            "&:hover": {
              color: isFileSelected() ? "#1976d2" : "gray",
            },
          }}
          onMouseEnter={() =>
            !isFileSelected() &&
            onStatusChange(
              isInRootFolder
                ? "Local inválido para baixar arquivos."
                : "Selecione um único arquivo para efetuar o Download."
            )
          }
          onMouseLeave={() => onStatusChange(null)}
          onClick={isFileSelected() ? handleDownloadClick : undefined}
        >
          <CloudDownloadIcon />
          <Typography variant="body2">Baixar</Typography>
        </Box>
      </Tooltip>

      <Box sx={{ borderLeft: "1px solid gray", height: "18px" }} />

      <Tooltip
        title={
          isInRootFolder
            ? "Local inválido para enviar arquivos."
            : selectedItems.size > 0
              ? "Nenhum item pode estar selecionado para efetuar Upload."
              : "Enviar (Upload)"
        }
        arrow
        placement="top"
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            cursor:
              selectedItems.size === 0 && !isInRootFolder
                ? "pointer"
                : "not-allowed",
            color:
              selectedItems.size === 0 && !isInRootFolder ? "inherit" : "gray",
            "&:hover": {
              color:
                selectedItems.size === 0 && !isInRootFolder
                  ? "#1976d2"
                  : "gray",
            },
          }}
          onMouseEnter={() => {
            setTimeout(() => {
              if (isInRootFolder) {
                onStatusChange("Local inválido para enviar arquivos.");
              } else if (selectedItems.size > 0) {
                onStatusChange(
                  "Nenhum item pode estar selecionado para efetuar Upload."
                );
              }
            }, 50);
          }}
          onMouseLeave={() => {
            setTimeout(() => onStatusChange(null), 50);
          }}
          onClick={() => {
            if (selectedItems.size === 0 && !isInRootFolder) {
              onUploadFile();
            }
          }}
        >
          <CloudUploadIcon />
          <Typography variant="body2">Enviar</Typography>
        </Box>
      </Tooltip>

      <Box sx={{ borderLeft: "1px solid gray", height: "18px" }} />

      <Tooltip
        title={
          selectedItems.size > 0
            ? "Nenhum item pode estar selecionado para Criar Pasta."
            : isInRootFolder
              ? "Local inválido para criar pasta."
              : "Criar Pasta / Diretório"
        }
        arrow
        placement="top"
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            cursor:
              selectedItems.size === 0 && !isInRootFolder
                ? "pointer"
                : "not-allowed",
            color:
              selectedItems.size === 0 && !isInRootFolder ? "inherit" : "gray",
            "&:hover": {
              color:
                selectedItems.size === 0 && !isInRootFolder
                  ? "#1976d2"
                  : "gray",
            },
          }}
          onMouseEnter={() => {
            setTimeout(() => {
              // Adicionamos um pequeno delay para garantir que o evento não seja suprimido
              if (selectedItems.size > 0) {
                onStatusChange(
                  "Nenhum item pode estar selecionado para Criar Pasta."
                );
              } else if (isInRootFolder) {
                onStatusChange("Local inválido para criar pasta.");
              }
            }, 50);
          }}
          onMouseLeave={() => {
            setTimeout(() => onStatusChange(null), 50);
          }}
          onClick={() => {
            if (selectedItems.size === 0 && !isInRootFolder) {
              onCreateFolder();
            }
          }}
        >
          <CreateNewFolderIcon />
          <Typography variant="body2">Criar Pasta</Typography>
        </Box>
      </Tooltip>

      <Box sx={{ borderLeft: "1px solid gray", height: "18px" }} />

      <Tooltip title="Atualizar" arrow placement="top">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            cursor: syncDisabled ? "default" : "pointer",
            color: syncDisabled ? "gray" : "inherit",
            "&:hover": {
              color: syncDisabled ? "gray" : "#1976d2",
            },
          }}
          onMouseEnter={() =>
            syncDisabled &&
            onStatusChange("Aguarde pelo 30 segundos para atualizar novamente.")
          }
          onMouseLeave={() => onStatusChange(null)}
          onClick={syncDisabled ? undefined : handleSync}
        >
          <CloudSyncIcon />
          <Typography variant="body2">Atualizar</Typography>
        </Box>
      </Tooltip>
    </Box>
  );
};

export default AppToolbar;
